import {createStore} from '@ngneat/elf';
import {deleteEntities, selectManyByPredicate, withEntities} from "@ngneat/elf-entities";
import {firstValueFrom, Observable} from "rxjs";
import {Injectable} from "@angular/core";
import {tap} from "rxjs/operators";
import {erstatteAlleEntiteterMedForelder, FeilhåndteringArgumenter, Feilhåndteringsstrategi, forelderIdPredikat, leggTilForelderId, oppdaterEntitetMedForelder, registrerEntiteterMedForelder, registrerEntitetMedForelder, serverFraLagerOgRequestPakketIMeta, serverFraRequest, serverFraRequestPakketIMeta, slettAlleEntiteterMedForeldre, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {AgendaelementId, BrukerId, KommentarId, KommentarModell, KommentarRessursClient, ModelloppdateringType, OppgaveId, OppgaveRessursClient, OpprettKommentarSpesifikasjon, OpprettKommentarSvarSpesifikasjon, SakRessursClient, SvarId} from "../ressurser/generertNg2HttpKlient";
import {kommentarerIkkePublisertAv, kommentarerPublisertAv, kommentarerPublisertIPeriode, sorterKommentarPåDato} from "felles-ng2/modell/kommentarModellHjelpere";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {ModellOpprettetEllerOppdatertHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellOpprettetEllerOppdatertHendelseHåndteringBygger";
import {ModellSlettetHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellSlettetHendelseHåndteringBygger";
import {DataOgMeta, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {skrivDatoPeriode, ZonedDateTimePeriode} from "felles-ng2/støttefunksjoner/zonedDateTimePerioder";
import {sorterLevertSamling} from "felles/ng2/rxjsHjelpere";
import {Modelloppdateringer, OppdateringmodellKommentarSlettet, OppdateringmodellNyKommentarOppgave, OppdateringmodellNyKommentarSak, OppdateringmodellNyttSvar, OppdateringmodellSvarSlettet} from "felles-ng2/tjenester/Modelloppdateringer";

const debug = initDebugger('KommentarDatatjeneste');

/** Kommentar forelder */
export type Forelder = AgendaelementId | OppgaveId;

/** Datalager for {@link KommentarModell} */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class KommentarDatatjeneste {
    /** for periodeAbonnenter bruker vi en fast nøkkel, alle modellendringer levert via {@link Modelloppdateringer} vil resultere i levering til abonnenter */
    private static periodeAbonnentNøkkel = 'alle';

    /** Lager for {@link KommentarModell}. Bruker 'kommentar.forelder.entitetRef.id' som forelder */
      //public for testbarhet
    readonly kommentarLager = createStore({name: 'kommentar'}, withEntities<KommentarModell>());

    /** Holder rede på åpne subscriptions basert på forelder (sak eller oppgave). Benyttes ifbm modelloppdateringer. */
      //public for testbarhet
    readonly forelderAbonnenter = new DatalagerAbonnenter('kommentar.forelder');
    /** Holder rede på åpne subscriptions basert på periode. Benyttes ifbm modelloppdateringer. */
      //public for testbarhet
    readonly periodeAbonnenter = new DatalagerAbonnenter('kommentar.periode');

    constructor(private kommentarRessurs: KommentarRessursClient,
                private sakRessurs: SakRessursClient,
                private oppgaveRessurs: OppgaveRessursClient,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser,
                private modelloppdateringer: Modelloppdateringer) {

        this.lyttTilModelloppdateringer();
    }

    /** Returnerer kommentarer tilknyttet angitt forelder registrert i lokallageret.
     * Forutsetter at relevante kommentarer har blitt lastet via andre datalagerkall. */
    finnTilknyttetFraLokallager(forelder: Forelder): Observable<KommentarModell[]> {
        return this.forelderAbonnenter.observer(forelder,
          this.kommentarLager.pipe(
            selectManyByPredicate(forelderIdPredikat(forelder)),
            sorterLevertSamling(KommentarDatatjeneste.sorterKommentarFunksjon))
        );
    }

    /** Erstatter alle kommentarer knytte til angitt forelder */
    erstattILokallager(kommentarer: KommentarModell[], forelder: Forelder) {
        erstatteAlleEntiteterMedForelder(this.kommentarLager, kommentarer, forelder);
    }

    mineKommentarerMedMeta(periode: ZonedDateTimePeriode, brukerId: BrukerId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<KommentarModell[]>> {
        const data: Observable<DataOgMeta<KommentarModell[]>> = serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.kommentarRessurs.finnKommentarerDerJegDeltarIPerioden(skrivDatoPeriode(periode)),
            registrerILager: respons => registrerEntiteterMedForelder(this.kommentarLager, respons.body!, this.forelderIdFraKommentar),
            hentFraLager: () => {
                return this.kommentarLager.pipe(
                  selectManyByPredicate((kommentar: KommentarModell) =>
                    kommentarerPublisertIPeriode(kommentar, periode)
                    && kommentarerPublisertAv(kommentar, brukerId)),
                  sorterLevertSamling(KommentarDatatjeneste.sorterKommentarFunksjon));
            },
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n("Kunne ikke hente kommentarer")), 'mineKommentarerMedMeta')
        });
        return this.periodeAbonnenter.observer(KommentarDatatjeneste.periodeAbonnentNøkkel, data);
    }

    mineKommentarer(periode: ZonedDateTimePeriode, brukerId: BrukerId, feilhåndteringsstrategi = Feilhåndteringsstrategi.visFeilmelding): Observable<KommentarModell[]> {
        return this.mineKommentarerMedMeta(periode, brukerId, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    andresKommentarerMedMeta(periode: ZonedDateTimePeriode, brukerId: BrukerId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<KommentarModell[]>> {
        const data: Observable<DataOgMeta<KommentarModell[]>> = serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.kommentarRessurs.finnKommentarerDerJegIkkeDeltarIPerioden(skrivDatoPeriode(periode)),
            registrerILager: respons => registrerEntiteterMedForelder(this.kommentarLager, respons.body!, this.forelderIdFraKommentar),
            hentFraLager: () => {
                return this.kommentarLager.pipe(
                  selectManyByPredicate((kommentar: KommentarModell) =>
                    kommentarerPublisertIPeriode(kommentar, periode)
                    && kommentarerIkkePublisertAv(kommentar, brukerId)),
                  sorterLevertSamling(KommentarDatatjeneste.sorterKommentarFunksjon));
            },
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n("Kunne ikke hente kommentarer")), 'andresKommentarerMedMeta')
        });
        return this.periodeAbonnenter.observer(KommentarDatatjeneste.periodeAbonnentNøkkel, data);
    }

    andresKommentarer(periode: ZonedDateTimePeriode, brukerId: BrukerId, feilhåndteringsstrategi = Feilhåndteringsstrategi.visFeilmelding): Observable<KommentarModell[]> {
        return this.mineKommentarerMedMeta(periode, brukerId, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    async opprettKommentarPåSak(sakId: AgendaelementId, spesifikasjon: OpprettKommentarSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<KommentarModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.sakRessurs.opprettKommentar(sakId, spesifikasjon).råRespons()
              .pipe(tap(respons => registrerEntitetMedForelder(this.kommentarLager, respons.body!, this.forelderIdFraKommentar))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere kommentar')), 'opprettKommentarPåSak')
        }));
    }

    async opprettKommentarPåOppgave(oppgaveId: OppgaveId, spesifikasjon: OpprettKommentarSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<KommentarModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.oppgaveRessurs.opprettKommentar(oppgaveId, spesifikasjon).råRespons()
              .pipe(tap(respons => registrerEntitetMedForelder(this.kommentarLager, respons.body!, this.forelderIdFraKommentar))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere kommentar')), 'opprettKommentarPåOppgave')
        }));
    }

    async opprettKommentarSvar(id: KommentarId, spesifikasjon: OpprettKommentarSvarSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<KommentarModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.kommentarRessurs.registrerSvar(id, spesifikasjon).råRespons()
              .pipe(tap(respons => oppdaterEntitetMedForelder(this.kommentarLager, respons.body!, this.forelderIdFraKommentar))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere svar på kommentar')), 'opprettKommentarSvar')
        }));
    }

    async gjørPrivat(id: KommentarId, privat: boolean, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<KommentarModell> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.kommentarRessurs.privat(id, {privat: privat}).råRespons()
              .pipe(tap(respons => oppdaterEntitetMedForelder(this.kommentarLager, respons.body!, this.forelderIdFraKommentar))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere kommentar')), 'gjørPrivat')
        }));
    }

    async slettKommentar(id: KommentarId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.kommentarRessurs.slettKommentar(id).råRespons()
              .pipe(tap(respons => this.kommentarLager.update(deleteEntities(id)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette kommentar')), 'slettKommentar')
        }));
    }

    /** Sletter alle kommentarer knyttet til angitte forelderIder i lokallager */
    slettKommentarerTilknyttetForelderILokallager(forelderIder: any[]) {
        slettAlleEntiteterMedForeldre(this.kommentarLager, forelderIder);
    }

    slettSvar(id: KommentarId, svarId: SvarId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<KommentarModell> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.kommentarRessurs.slettSvar(id, svarId).råRespons()
              .pipe(tap(respons => oppdaterEntitetMedForelder(this.kommentarLager, respons.body!, this.forelderIdFraKommentar))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette svar')), 'slettSvar')
        }));
    }

    //hjelpemetoder

    private forelderIdFraKommentar(kommentar: KommentarModell) {
        return kommentar.forelder.entitetRef.id;
    }

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(
      feilhåndteringsstrategi: STRATEGI,
      brukerrettetFeilmelding: string,
      metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    /**
     * Lytt til relevante modelloppdateringer via {@link Modelloppdateringer} (push-baserte oppdateringer).
     * Merk at vi aldri avregistrerer subscription vi oppretter her da data-tjenesten aldri termineres.
     */
    private lyttTilModelloppdateringer() {
        this.lyttTilNyttSvar();
        this.lyttTilKommentarSlettet();
        this.lyttTilSvarSlettet();
        this.lyttTilNyKommentarOppgave();
        this.lyttTilNyKommentarSak();
    }

    private lyttTilNyttSvar() {
        const bygger = this.opprettetEllerOppdatertHendelse<OppdateringmodellNyttSvar>(ModelloppdateringType.NYTT_SVAR);
        bygger
          .medAbonnentoppslag(this.forelderAbonnenter, hendelseModell => hendelseModell.forelderId)
          .medAbonnentoppslag(this.periodeAbonnenter, () => KommentarDatatjeneste.periodeAbonnentNøkkel)
          .oppdaterLagerVedHendelse();
    }

    private lyttTilKommentarSlettet() {
        this.slettetHendelse<OppdateringmodellKommentarSlettet>(ModelloppdateringType.KOMMENTAR_SLETTET)
          .oppdaterLagerVedHendelse();
    }

    private lyttTilSvarSlettet() {
        this.slettetHendelse<OppdateringmodellSvarSlettet>(ModelloppdateringType.SVAR_SLETTET)
          .oppdaterLagerVedHendelse();
    }

    private lyttTilNyKommentarOppgave() {
        this.opprettetEllerOppdatertHendelse<OppdateringmodellNyKommentarOppgave>(ModelloppdateringType.NY_KOMMENTAR_OPPGAVE)
          .medAbonnentoppslag(this.forelderAbonnenter, hendelseModell => hendelseModell.oppgaveId)
          .medAbonnentoppslag(this.periodeAbonnenter, () => KommentarDatatjeneste.periodeAbonnentNøkkel)
          .oppdaterLagerVedHendelse();
    }

    private lyttTilNyKommentarSak() {
        this.opprettetEllerOppdatertHendelse<OppdateringmodellNyKommentarSak>(ModelloppdateringType.NY_KOMMENTAR_SAK)
          .medAbonnentoppslag(this.forelderAbonnenter, hendelseModell => hendelseModell.forelderId)
          .medAbonnentoppslag(this.periodeAbonnenter, () => KommentarDatatjeneste.periodeAbonnentNøkkel)
          .oppdaterLagerVedHendelse();
    }

    private opprettetEllerOppdatertHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellMedKommentar>(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, KommentarModell>(this.kommentarLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetLaster(hendelseModell => this.kommentarRessurs.hent(hendelseModell.kommentarId).råRespons())
          .medForberedLageroppdatering(kommentar => leggTilForelderId(kommentar, this.forelderIdFraKommentar(kommentar)))
          .medEntitetId(hendelseModell => hendelseModell.kommentarId);
    }

    private slettetHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellMedKommentar>(hendelseType: ModelloppdateringType) {
        return new ModellSlettetHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, KommentarModell>(this.kommentarLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell.kommentarId);
    }

    static sorterKommentarFunksjon = sorterKommentarPåDato;

}

type OppdateringmodellMedKommentar = { kommentarId: KommentarId };
